import { useState, useRef, useCallback, useEffect, useMemo, memo } from "react";
import { MessageList } from "react-chat-elements";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import axios from "axios";
import * as XLSX from "xlsx";
import "react-chat-elements/dist/main.css";
import "./App.css";
import { API_ENDPOINT } from "./config/api";
import { generateRequestId } from "./utils/requestId";

// Create axios instance with credentials
const apiClient = axios.create({
  baseURL: API_ENDPOINT,
  withCredentials: true, // Important for cookies
});

interface Message {
  id: string;
  position: "left" | "right";
  type: "text";
  text: string;
  date: Date;
  avatar?: string;
  title: string;
  focus: boolean;
  titleColor: string;
  forwarded: boolean;
  replyButton: boolean;
  removeButton: boolean;
  status: "sent" | "waiting" | "received" | "read";
  notch: boolean;
  retracted: boolean;
}

const OPENAI_MODELS = [
  { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
  { value: "gpt-4", label: "GPT-4" },
  { value: "gpt-4o-mini", label: "GPT-4o Mini" },
  { value: "gpt-4o", label: "GPT-4o" },
];

// Table component with xlsx download button
const MarkdownTableWithDownload = ({ children }: { children: React.ReactNode }) => {
  const tableRef = useRef<HTMLTableElement>(null);

  const downloadXlsx = () => {
    if (!tableRef.current) return;
    const table = tableRef.current;
    const rows: string[][] = [];

    // Extract header row
    const headerCells = table.querySelectorAll("thead tr th");
    if (headerCells.length > 0) {
      rows.push(Array.from(headerCells).map((th) => th.textContent || ""));
    }

    // Extract body rows
    table.querySelectorAll("tbody tr").forEach((tr) => {
      const cells = Array.from(tr.querySelectorAll("td")).map(
        (td) => td.textContent || ""
      );
      rows.push(cells);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Datos");
    XLSX.writeFile(wb, "tabla.xlsx");
  };

  const downloadButton = (
    <button
      onClick={downloadXlsx}
      className="download-xlsx-btn"
      title="Descargar como Excel"
    >
      <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
      </svg>
      Descargar XLSX
    </button>
  );

  return (
    <div className="markdown-table-wrapper">
      <div className="markdown-table-toolbar">{downloadButton}</div>
      <table ref={tableRef} className="markdown-table">
        {children}
      </table>
      <div className="markdown-table-toolbar">{downloadButton}</div>
    </div>
  );
};

// Memoized individual message component to prevent re-renders
const MessageItem = memo(({ message }: { message: Message }) => {
  return (
    <div className={`message-wrapper ${message.position}`}>
      <div className="message-content">
        <div className="message-title">{message.title}</div>
        <div className={`message-bubble ${message.position}`}>
          {message.position === "left" ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <p className="markdown-p">{children}</p>,
                ul: ({ children }) => (
                  <ul className="markdown-ul">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="markdown-ol">{children}</ol>
                ),
                li: ({ children }) => (
                  <li className="markdown-li">{children}</li>
                ),
                code: ({ children }) => (
                  <code className="markdown-code">{children}</code>
                ),
                pre: ({ children }) => (
                  <pre className="markdown-pre">{children}</pre>
                ),
                strong: ({ children }) => <strong>{children}</strong>,
                em: ({ children }) => <em>{children}</em>,
                table: ({ children }) => (
                  <MarkdownTableWithDownload>{children}</MarkdownTableWithDownload>
                ),
                thead: ({ children }) => (
                  <thead className="markdown-thead">{children}</thead>
                ),
                tbody: ({ children }) => (
                  <tbody className="markdown-tbody">{children}</tbody>
                ),
                tr: ({ children }) => (
                  <tr className="markdown-tr">{children}</tr>
                ),
                th: ({ children }) => (
                  <th className="markdown-th">{children}</th>
                ),
                td: ({ children }) => (
                  <td className="markdown-td">{children}</td>
                ),
              }}
            >
              {message.text}
            </ReactMarkdown>
          ) : (
            message.text
          )}
        </div>
        <div className="message-time">{message.date.toLocaleTimeString()}</div>
      </div>
    </div>
  );
});

MessageItem.displayName = "MessageItem";

const CustomMessageList = ({
  messages,
  isLoading,
}: {
  messages: Message[];
  isLoading: boolean;
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <div className="custom-message-list">
      {messages.map((message) => (
        <MessageItem key={message.id} message={message} />
      ))}
      {isLoading && (
        <div className="message-wrapper left">
          <div className="message-content">
            <div className="message-title">Bot</div>
            <div className="message-bubble left typing">
              <div className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};

// Login Component
const LoginScreen = ({
  onLoginSuccess,
}: {
  onLoginSuccess: (token: string) => void;
}) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await apiClient.post("/login", {
        username,
        password,
      });

      const { access_token } = response.data;
      onLoginSuccess(access_token);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.detail ||
            "Error al iniciar sesión. Verifica tus credenciales."
        );
      } else {
        setError("Error inesperado. Por favor, intenta de nuevo.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h2>Chatbot de Consultas Triunfo</h2>
          <p>Inicia sesión para continuar</p>
        </div>
        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Usuario</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Ingresa tu usuario"
              disabled={isLoading}
              required
              autoComplete="username"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Ingresa tu contraseña"
              disabled={isLoading}
              required
              autoComplete="current-password"
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? "Iniciando sesión..." : "Iniciar sesión"}
          </button>
        </form>
      </div>
    </div>
  );
};

function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedModel, setSelectedModel] = useState(() => {
    // Load from localStorage or default to gpt-4o-mini
    return localStorage.getItem("selectedModel") || OPENAI_MODELS[2].value;
  });
  const messageListRef = useRef<any>(null);

  // Authentication state
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);

  // Save selectedModel to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("selectedModel", selectedModel);
  }, [selectedModel]);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Try to refresh token (will use HttpOnly cookie)
      const response = await apiClient.post("/refresh");
      const { access_token } = response.data;
      setAccessToken(access_token);
      console.log("Authentication successful via refresh token");
    } catch (error) {
      console.log("No valid session found, user needs to login");
      setAccessToken(null);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleLoginSuccess = (token: string) => {
    setAccessToken(token);
  };

  const handleLogout = async () => {
    try {
      await apiClient.post("/logout");
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      setAccessToken(null);
      setMessages([]);
      console.log("Logged out successfully");
    }
  };

  // Helper to convert Message[] to OpenAI-style previous_messages (memoized)
  const getPreviousMessages = useMemo(() => {
    // Always start with the system prompt as first message
    const systemMessage = {
      role: "system",
      content:
        "Sos un asistente con acceso a producto descripción y cant en stock.",
    };
    // Map chat messages to OpenAI roles
    const chatMessages = messages.map((msg) => ({
      role: msg.position === "right" ? "user" : "assistant",
      content: msg.text,
    }));
    return [systemMessage, ...chatMessages];
  }, [messages]);

  // Memoized input change handler
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const addMessage = (text: string, position: "left" | "right") => {
    const newMessage: Message = {
      id: Date.now().toString(),
      position,
      type: "text",
      text,
      date: new Date(),
      avatar: position === "left" ? "/bot-avatar.png" : "/user-avatar.png",
      title: position === "left" ? "Bot" : "You",
      focus: false,
      titleColor: position === "left" ? "#007bff" : "#28a745",
      forwarded: false,
      replyButton: false,
      removeButton: false,
      status: "sent",
      notch: true,
      retracted: false,
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const sendMessage = useCallback(async () => {
    const message = inputValue.trim();
    if (!message || isLoading) return;

    // Generate unique request ID
    const requestId = generateRequestId();
    console.log(
      `[${requestId}] Sending request with prompt: "${message.substring(
        0,
        50
      )}..."`
    );

    // Add user message
    addMessage(message, "right");
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await apiClient.post(
        "/ask",
        {
          prompt: message,
          previous_messages: getPreviousMessages,
          model: selectedModel,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Request-ID": requestId,
            Authorization: `Bearer ${accessToken}`,
          },
          timeout: 1800000, // 30 minutes timeout
        }
      );

      console.log(`[${requestId}] Request completed successfully`);
      // Add bot response
      addMessage(response.data.answer, "left");
    } catch (error) {
      console.error(`[${requestId}] Error sending message:`, error);

      // Check if token expired
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        // Try to refresh token
        try {
          const refreshResponse = await apiClient.post("/refresh");
          const { access_token } = refreshResponse.data;
          setAccessToken(access_token);

          // Retry the original request
          const retryResponse = await apiClient.post(
            "/ask",
            {
              prompt: message,
              previous_messages: getPreviousMessages,
              model: selectedModel,
            },
            {
              headers: {
                "Content-Type": "application/json",
                "X-Request-ID": requestId,
                Authorization: `Bearer ${access_token}`,
              },
              timeout: 1800000,
            }
          );
          addMessage(retryResponse.data.answer, "left");
          return;
        } catch (refreshError) {
          // Refresh failed, need to login again
          setAccessToken(null);
          addMessage(
            "Tu sesión ha expirado. Por favor, inicia sesión de nuevo.",
            "left"
          );
          return;
        }
      }

      // Show user-friendly error with request ID
      const errorMessage = axios.isAxiosError(error)
        ? `Lo siento, hubo un error al procesar tu mensaje. ID de referencia: ${requestId}. Por favor, intenta de nuevo o comparte este ID con soporte.`
        : `Error inesperado. ID de referencia: ${requestId}. Por favor, contacta a soporte.`;

      addMessage(errorMessage, "left");
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, messages, selectedModel, accessToken]);

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Show loading state while checking authentication
  if (isAuthenticating) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Verificando autenticación...</p>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!accessToken) {
    return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="App">
      <div className="chat-container">
        <div className="chat-header">
          <h2>Chatbot de Consultas Triunfo</h2>
          <button onClick={handleLogout} className="logout-button">
            Cerrar sesión
          </button>
        </div>

        <div className="messages-container">
          <CustomMessageList messages={messages} isLoading={isLoading} />
          {/*<MessageList
            referance={messageListRef}
            className="message-list"
            lockable={true}
            toBottomHeight={"80%"}
            dataSource={messages}
          />*/}
          {isLoading && (
            <div className="typing-indicator">
              <div className="typing-indicator-avatar">AI</div>
              <div className="typing-text">
                Pensando...
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="input-container">
          <div className="custom-input-wrapper">
            <textarea
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Enviar mensaje al asistente..."
              disabled={isLoading}
              className="chat-input"
              rows={4}
            />
            <select
              id="model-select"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              style={{
                padding: "8px 10px",
                borderRadius: 8,
                border: "1px solid #ccc",
                fontSize: 14,
                marginRight: 8,
                background: "#40414f",
                color: "#ececf1",
                minWidth: 120,
                height: 40,
              }}
              disabled={isLoading}
            >
              {OPENAI_MODELS.map((model) => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
            <button
              onClick={sendMessage}
              disabled={isLoading || !inputValue.trim()}
              className="send-button"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
